<template>
    <el-dialog
        :visible.sync="dialogVisible"
        :title="dialogTitle"
        custom-class="dialog-no-border"
        :width="currentStep === 2 ? '70%' : '640px'"
        :close-on-click-modal="false"
        :before-close="handleBeforeClose"
    >
        <div slot="title">
            <h5 class="mb-0">{{ dialogTitle }}</h5>
        </div>

        <!-- Step indicator (submit mode only, enhanced only) -->
        <el-steps
            v-if="!isViewMode && form.type === TYPE_ENHANCED"
            :active="currentStep - 1"
            align-center
            finish-status="success"
            class="mb-3"
        >
            <el-step title="Details" />
            <el-step title="Phone Numbers" />
        </el-steps>

        <!-- Step 1: Form Details -->
        <el-form
            v-show="currentStep === 1"
            ref="brandedCallerIdForm"
            :model="form"
            :rules="isViewMode ? {} : rules"
            v-loading="submitting || loading"
            label-position="top"
        >
            <!-- Type selector (submit mode only) -->
            <el-form-item v-if="!isViewMode" prop="type" label="Branded Caller ID Type">
                <el-radio-group v-model="form.type" @change="onTypeChange">
                    <el-radio :label="TYPE_ENHANCED">
                        Enhanced Branded Caller ID
                        <el-tag size="mini" type="warning" effect="dark" class="ml-1" style="vertical-align: middle;">Recommended</el-tag>
                    </el-radio>
                    <el-radio :label="TYPE_STANDARD">Standard Branded Caller ID</el-radio>
                </el-radio-group>
            </el-form-item>

            <!-- View mode: show type badge -->
            <div v-if="isViewMode" class="mb-3">
                <span
                    :class="form.type === TYPE_ENHANCED ? 'badge-enhanced' : 'badge-standard'"
                    class="type-badge"
                >
                    {{ form.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard' }} Branded Caller ID
                </span>
            </div>

            <el-alert
                v-if="!isViewMode"
                type="info"
                :closable="false"
                show-icon
                class="mb-3 prerequisites-alert"
            >
                <template slot="title">Prerequisites</template>
                You must have an <strong>approved Voice Integrity bundle</strong> and a <strong>signed Letter of Authorization (LOA)</strong> to submit.
            </el-alert>

            <!-- Voice Integrity bundle selector -->
            <el-form-item prop="voice_integrity_id">
                <template slot="label">
                    Voice Integrity Bundle
                    <el-tooltip class="item" effect="dark" content="An approved Voice Integrity bundle is required as the base trust product for Branded Caller ID registration." placement="top">
                        <i class="fa fa-info-circle text-blue"/>
                    </el-tooltip>
                </template>
                <el-select
                    v-model="form.voice_integrity_id"
                    placeholder="Select an approved Voice Integrity bundle"
                    class="w-full"
                    :disabled="isViewMode"
                    @change="preValidateForm('brandedCallerIdForm')"
                >
                    <el-option
                        v-for="bundle in voiceIntegrityBundles"
                        :key="bundle.id"
                        :label="bundle.friendly_name"
                        :value="bundle.id"
                    />
                </el-select>
            </el-form-item>

            <!-- Display Name -->
            <el-form-item prop="display_name">
                <template slot="label">
                    Display Name
                    <el-tooltip
                        class="item"
                        effect="dark"
                        :content="form.type === TYPE_ENHANCED
                            ? 'Max 35 characters. Use your official legal name, brand name, or DBA. Must start with a letter. No emojis, PII, or non-ASCII characters.'
                            : 'Max 15 characters. Use your official legal name, brand name, or DBA. Must start with a letter. No emojis, PII, or non-ASCII characters.'"
                        placement="top"
                    >
                        <i class="fa fa-info-circle text-blue"/>
                    </el-tooltip>
                </template>
                <el-input
                    v-model="form.display_name"
                    :maxlength="form.type === TYPE_ENHANCED ? 35 : 15"
                    show-word-limit
                    placeholder="Your Company"
                    :disabled="isViewMode"
                    @input="preValidateForm('brandedCallerIdForm')"
                />
            </el-form-item>

            <!-- Display Long Name (Standard only) -->
            <el-form-item v-if="form.type === TYPE_STANDARD" prop="display_long_name">
                <template slot="label">
                    Display Long Name
                    <el-tooltip
                        class="item"
                        effect="dark"
                        content="Maximum 32 characters. Must start with a letter. Can contain letters, numbers, periods, commas, and spaces."
                        placement="top"
                    >
                        <i class="fa fa-info-circle text-blue"/>
                    </el-tooltip>
                </template>
                <el-input
                    v-model="form.display_long_name"
                    maxlength="32"
                    show-word-limit
                    placeholder="Your Company, Inc."
                    :disabled="isViewMode"
                    @input="preValidateForm('brandedCallerIdForm')"
                />
            </el-form-item>

            <!-- Enhanced-only fields -->
            <template v-if="form.type === TYPE_ENHANCED">
                <el-form-item prop="display_call_reason">
                    <template slot="label">
                        Display Call Reason
                        <el-tooltip class="item" effect="dark" content="Max 64 characters; 35 or fewer recommended for best mobile display. Describes the purpose of the call shown to the recipient. Not available on all devices." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-input
                        v-model="form.display_call_reason"
                        maxlength="64"
                        show-word-limit
                        placeholder="e.g. Account notification"
                        :disabled="isViewMode"
                        @input="preValidateForm('brandedCallerIdForm')"
                    />
                </el-form-item>

                <el-form-item prop="display_logo_url">
                    <template slot="label">
                        Display Logo URL
                        <el-tooltip class="item" effect="dark" content="Public HTTPS URL to your logo image. Must be a 32-bit BMP, 256×256 pixels. Vetted against trademark registries. Display varies by device and carrier." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-input
                        v-model="form.display_logo_url"
                        placeholder="https://example.com/logo.png"
                        :disabled="isViewMode"
                        @input="preValidateForm('brandedCallerIdForm')"
                        @blur="() => { display_logo_url = display_logo_url; preValidateForm('brandedCallerIdForm'); }"
                    />
                </el-form-item>

                <el-form-item prop="privacy_notice_url">
                    <template slot="label">
                        Privacy Notice URL
                        <el-tooltip class="item" effect="dark" content="Your company's publicly accessible privacy policy URL. Must be a valid HTTPS link that end users can visit." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-input
                        v-model="form.privacy_notice_url"
                        placeholder="https://example.com/privacy"
                        :disabled="isViewMode"
                        @input="preValidateForm('brandedCallerIdForm')"
                        @blur="() => { privacy_notice_url = privacy_notice_url; preValidateForm('brandedCallerIdForm'); }"
                    />
                </el-form-item>

                <!-- Use Case section -->
                <div class="section-header mt-3 mb-2">
                    <strong>Use Case</strong>
                </div>

                <el-form-item prop="use_case.category">
                    <template slot="label">
                        Category
                        <el-tooltip class="item" effect="dark" content="Select the category that best describes the purpose of your outbound calls. This determines how Twilio classifies your Branded Caller ID registration." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-select
                        v-model="form.use_case.category"
                        placeholder="Select a use case category"
                        class="w-full"
                        :disabled="isViewMode"
                        @change="preValidateForm('brandedCallerIdForm')"
                    >
                        <el-option v-for="cat in useCaseCategories" :key="cat.value" :label="cat.label" :value="cat.value"/>
                    </el-select>
                </el-form-item>

                <el-form-item prop="use_case.description">
                    <template slot="label">
                        Use Case Description
                        <el-tooltip class="item" effect="dark" content="Explain how you intend to use Branded Calling — the nature of your outbound calls, who you call, and why. Be specific; this is reviewed by Twilio." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-input
                        v-model="form.use_case.description"
                        type="textarea"
                        :rows="3"
                        placeholder="Describe how you will use branded calling..."
                        :disabled="isViewMode"
                        @input="preValidateForm('brandedCallerIdForm')"
                    />
                </el-form-item>

                <el-form-item prop="use_case.consent_description">
                    <template slot="label">
                        Consent Description
                        <el-tooltip class="item" effect="dark" content="Describe how your recipients have opted in or consented to receive calls from you (e.g. via a web form, verbal agreement, signed contract). Required by Twilio." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-input
                        v-model="form.use_case.consent_description"
                        type="textarea"
                        :rows="3"
                        placeholder="Describe how recipients have consented to receive calls..."
                        :disabled="isViewMode"
                        @input="preValidateForm('brandedCallerIdForm')"
                    />
                </el-form-item>

                <el-form-item prop="use_case.category_apple">
                    <template slot="label">
                        Apple Business Connect Category (optional)
                        <el-tooltip class="item" effect="dark" content="Optional. Maps your use case to an Apple Business Connect category for display on Apple devices. Leave blank if you don't use Apple Business Connect." placement="top">
                            <i class="fa fa-info-circle text-blue"/>
                        </el-tooltip>
                    </template>
                    <el-input
                        v-model="form.use_case.category_apple"
                        placeholder="Optional Apple category"
                        :disabled="isViewMode"
                        @input="preValidateForm('brandedCallerIdForm')"
                    />
                </el-form-item>
            </template>

            <!-- LOA file upload -->
            <el-form-item prop="loa_file" label="Letter of Authorization (LOA)">
                <template slot="label">
                    Letter of Authorization (LOA)
                    <el-tooltip
                        v-if="!isViewMode"
                        class="item"
                        effect="dark"
                        content="Upload a signed LOA document (PDF, JPG, JPEG, or PNG). Maximum file size: 2MB."
                        placement="top"
                    >
                        <i class="fa fa-info-circle text-blue"/>
                    </el-tooltip>
                </template>
                <div v-if="isViewMode && form.loa_file_url">
                    <p class="mb-0">
                        <a
                            :href="form.loa_file_url"
                            target="_blank"
                            class="text-blue font-weight-bold"
                        >
                            View Document
                        </a>
                    </p>
                </div>
                <el-upload
                    v-else-if="!isViewMode"
                    ref="loaUpload"
                    :auto-upload="false"
                    :limit="1"
                    :on-change="handleFileChange"
                    :on-remove="handleFileRemove"
                    :file-list="fileList"
                    accept=".pdf,.jpg,.jpeg,.png"
                    action="#"
                    class="my-3"
                >
                    <el-button size="small" type="primary">
                        <i class="fa fa-upload mr-1"></i>
                        Select LOA File
                    </el-button>
                    <div slot="tip" class="el-upload__tip">
                        Supported formats: PDF, JPG, JPEG, PNG (Max: 2MB)<br>
                        <a href="https://cdn.prod.website-files.com/67ac801aadc0bbd4b1d89967/69d515dbbb0a7c864b94e6df_Aloware_Branded_Calling_LOA%20(1).pdf" target="_blank" class="text-blue">
                            <i class="fa fa-download mr-1"></i>Download LOA sample template
                        </a>
                    </div>
                </el-upload>
            </el-form-item>

            <div v-if="!isViewMode" class="row mt-4">
                <div class="col-md-12">
                    <el-button
                        v-if="form.type === TYPE_ENHANCED"
                        type="primary"
                        @click="goToStep2"
                        class="w-full"
                        :disabled="!validated"
                    >
                        Next: Select Phone Numbers
                    </el-button>
                    <el-button
                        v-else
                        type="primary"
                        @click="submitBrandedCallerId"
                        class="w-full"
                        :loading="submitting"
                        :disabled="!validated"
                    >
                        Submit Standard Branded Caller ID
                    </el-button>
                </div>
            </div>
        </el-form>

        <!-- Step 2: Phone Number Assignment (Enhanced only) -->
        <div v-if="currentStep === 2 && !isViewMode" v-loading="submitting">
            <el-alert
                type="info"
                :closable="false"
                show-icon
                class="mb-3"
            >
                <template slot="title">
                    <span class="break-word text-dark">
                        Select phone numbers to assign before submitting for review (optional).
                        Numbers cannot be assigned while the bundle is in review.
                    </span>
                </template>
            </el-alert>

            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <el-button
                        type="success"
                        size="small"
                        @click="selectAllNumbers"
                        :disabled="loadingNumbers"
                    >
                        Select All
                    </el-button>
                    <el-button
                        size="small"
                        @click="clearNumberSelection"
                        :disabled="selectedPhoneNumberIds.length === 0"
                    >
                        Clear Selection
                    </el-button>
                    <span v-if="selectedPhoneNumberIds.length > 0" class="ml-2 text-muted" style="font-size: 13px;">
                        {{ selectedPhoneNumberIds.length }} number(s) selected
                    </span>
                </div>
                <el-input
                    v-model="numberSearch"
                    placeholder="Search by phone number, SID, or line name..."
                    size="small"
                    clearable
                    prefix-icon="el-icon-search"
                    style="width: 280px;"
                />
            </div>

            <el-table
                ref="numberTable"
                :data="availableNumbers"
                v-loading="loadingNumbers"
                row-key="id"
                @selection-change="handleNumberSelectionChange"
                style="width: 100%"
            >
                <template slot="empty">
                    <span class="text-muted">
                        {{ numberSearch ? 'No numbers match your search.' : 'No unregistered numbers available.' }}
                    </span>
                </template>
                <el-table-column type="selection" width="55" :reserve-selection="true" />
                <el-table-column prop="id" label="ID" width="80" />
                <el-table-column prop="phone_number" label="Phone Number" width="150">
                    <template v-slot="scope">
                        {{ scope.row.phone_number | fixPhone }}
                    </template>
                </el-table-column>
                <el-table-column prop="sid" label="SID" min-width="180" show-overflow-tooltip>
                    <template v-slot="scope">
                        {{ scope.row.sid || '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="Line" min-width="150" show-overflow-tooltip>
                    <template v-slot="scope">
                        <span v-if="scope.row.campaign">{{ scope.row.campaign.name || `#${scope.row.campaign.id}` }}</span>
                        <span v-else class="text-muted">-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="country" label="Country" width="90">
                    <template v-slot="scope">{{ scope.row.country || '-' }}</template>
                </el-table-column>
            </el-table>

            <div class="d-flex justify-content-between align-items-center mt-3">
                <span class="text-muted">
                    Total: {{ numberPagination.total }} unregistered number(s)
                </span>
                <el-pagination
                    v-if="numberPagination.total > numberPagination.size"
                    layout="sizes, prev, pager, next"
                    :current-page.sync="numberPagination.page"
                    :page-sizes="[10, 20, 50, 100]"
                    :page-size.sync="numberPagination.size"
                    :total="numberPagination.total"
                    :disabled="loadingNumbers"
                    @size-change="handleNumberSizeChange"
                    @current-change="fetchAvailableNumbers"
                />
            </div>

            <div class="row mt-4">
                <div class="col-md-6">
                    <el-button
                        @click="currentStep = 1"
                        class="w-full"
                    >
                        Back
                    </el-button>
                </div>
                <div class="col-md-6">
                    <el-button
                        type="primary"
                        @click="submitBrandedCallerId"
                        class="w-full"
                        :loading="submitting"
                    >
                        Submit Enhanced Branded Caller ID
                    </el-button>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import {form_validation_mixin} from '../../../mixins'
import {TYPE_STANDARD, TYPE_ENHANCED, USE_CASE_CATEGORIES} from '../../../constants/branded-caller-id-use-cases'

export default {
    name: 'SubmitBrandedCallerIdDialog',
    mixins: [form_validation_mixin],
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        company: {
            type: Object,
            required: true
        },
        voiceIntegrityBundles: {
            type: Array,
            required: true
        },
        bundleId: {
            type: Number,
            default: null
        }
    },
    computed: {
        isViewMode() {
            return this.bundleId !== null
        },
        dialogVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            }
        },
        display_logo_url: {
            get() {
                return this.form.display_logo_url
            },
            set(value) {
                this.form.display_logo_url = this.transformUrl(value)
            },
        },
        privacy_notice_url: {
            get() {
                return this.form.privacy_notice_url
            },
            set(value) {
                this.form.privacy_notice_url = this.transformUrl(value)
            },
        },
        dialogTitle() {
            if (this.isViewMode) {
                return 'Branded Caller ID Details'
            }
            if (this.currentStep === 2) {
                return 'Select Phone Numbers — Enhanced Branded Caller ID'
            }
            return this.form.type === TYPE_ENHANCED
                ? 'Submit Enhanced Branded Caller ID'
                : 'Submit Standard Branded Caller ID'
        },
        rules() {
            const isEnhanced = this.form.type === TYPE_ENHANCED
            const base = {
                type: [{required: true, message: 'Please select a type', trigger: 'change'}],
                voice_integrity_id: [{required: true, message: 'Please select a Voice Integrity bundle', trigger: 'change'}],
                display_name: [
                    {required: true, message: 'Display name is required', trigger: 'blur'},
                    {
                        min: 2,
                        max: isEnhanced ? 35 : 15,
                        message: `Display name must be between 2 and ${isEnhanced ? 35 : 15} characters`,
                        trigger: 'blur'
                    },
                    {
                        // Must start with a letter. Only letters, numbers, periods, commas, and spaces. Max 35 chars (enhanced) / 15 chars (standard).
                        pattern: isEnhanced
                            ? /^[a-zA-Z][A-Za-z0-9,.\s]{1,34}$/
                            : /^[a-zA-Z][A-Za-z0-9,.\s]{1,14}$/,
                        message: 'Display name must start with a letter and can only contain letters, numbers, periods, commas, and spaces',
                        trigger: 'blur'
                    },
                ],
                display_long_name: null,
                loa_file: [
                    {required: true, message: 'Please upload a Letter of Authorization (LOA) file', trigger: 'change'},
                    {
                        validator: (_rule, value, callback) => {
                            if (!value) {
                                callback(new Error('Please upload an LOA file'))
                            } else if (value.size > 2 * 1024 * 1024) {
                                callback(new Error('File size must not exceed 2MB'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'change',
                    },
                ],
            }

            if (!isEnhanced) {
                base.display_long_name = [
                    {required: true, message: 'Display long name is required', trigger: 'blur'},
                    {min: 2, max: 32, message: 'Display long name must be between 2 and 32 characters', trigger: 'blur'},
                    {
                        // Must start with a letter. Only letters, numbers, periods, commas, and spaces. Max 32 chars.
                        pattern: /^[a-zA-Z][A-Za-z0-9,.\s]{1,31}$/,
                        message: 'Display long name must start with a letter and can only contain letters, numbers, periods, commas, and spaces',
                        trigger: 'blur'
                    },
                ]
            } else {
                base['use_case.category'] = [{required: true, message: 'Use case category is required', trigger: 'change'}]
                base['use_case.description'] = [{required: true, message: 'Use case description is required', trigger: 'blur'}]
                base['use_case.consent_description'] = [{required: true, message: 'Consent description is required', trigger: 'blur'}]
                base.display_call_reason = [
                    {required: true, message: 'Display call reason is required', trigger: 'blur'},
                    {max: 64, message: 'Display call reason may not be greater than 64 characters', trigger: 'blur'},
                    {
                        // Only letters, numbers, periods, commas, and spaces. Max 64 chars. No leading-letter requirement (matches Twilio schema).
                        pattern: /^[A-Za-z0-9., ]{1,64}$/,
                        message: 'Display call reason can only contain letters, numbers, periods, commas, and spaces',
                        trigger: 'blur',
                    },
                ]
                base.display_logo_url = [
                    {required: true, message: 'Display logo URL is required', trigger: 'blur'},
                    {
                        validator: (_rule, value, callback) => {
                            if (typeof value !== 'string' || !value.startsWith('https://')) {
                                callback(new Error('Display logo URL must start with https://'))
                            } else if (value === 'https://') {
                                callback(new Error('Display logo URL must include a domain after https://'))
                            } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                                callback(new Error('Display logo URL must be a valid URL'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'blur',
                    },
                    {max: 2048, message: 'URL may not be greater than 2048 characters', trigger: 'blur'},
                ]
                base.privacy_notice_url = [
                    {required: true, message: 'Privacy notice URL is required', trigger: 'blur'},
                    {
                        validator: (_rule, value, callback) => {
                            if (typeof value !== 'string' || !value.startsWith('https://')) {
                                callback(new Error('Privacy notice URL must start with https://'))
                            } else if (value === 'https://') {
                                callback(new Error('Privacy notice URL must include a domain after https://'))
                            } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                                callback(new Error('Privacy notice URL must be a valid URL'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'blur',
                    },
                    {max: 2048, message: 'URL may not be greater than 2048 characters', trigger: 'blur'},
                ]
            }

            return base
        },
    },
    data() {
        return {
            currentStep: 1,
            submitting: false,
            loading: false,
            fileList: [],
            TYPE_STANDARD,
            TYPE_ENHANCED,
            useCaseCategories: USE_CASE_CATEGORIES,
            form: this.defaultForm(),
            availableNumbers: [],
            loadingNumbers: false,
            selectedPhoneNumberIds: [],
            numberSearch: '',
            numberSearchTimeout: null,
            numberPagination: { page: 1, size: 10, total: 0 },
        }
    },
    watch: {
        numberSearch(val) {
            clearTimeout(this.numberSearchTimeout)
            if (val.length >= 3 || val.length === 0) {
                this.numberSearchTimeout = setTimeout(() => {
                    this.fetchAvailableNumbers(1)
                }, 400)
            }
        },
        visible(isVisible) {
            if (isVisible) {
                if (this.isViewMode) {
                    this.$nextTick(() => {
                        if (this.$refs.brandedCallerIdForm) {
                            this.$refs.brandedCallerIdForm.clearValidate()
                        }
                    })
                    this.loadBundleData()
                } else {
                    this.resetForm()
                    this.preValidateForm('brandedCallerIdForm')
                }
            }
        },
    },
    methods: {
        defaultForm() {
            return {
                type: TYPE_ENHANCED,
                voice_integrity_id: null,
                display_name: '',
                display_long_name: '',
                loa_file: null,
                loa_file_url: null,
                display_call_reason: '',
                display_logo_url: '',
                privacy_notice_url: '',
                use_case: {
                    category: '',
                    description: '',
                    consent_description: '',
                    category_apple: '',
                },
            }
        },
        handleBeforeClose(done) {
            if (this.isViewMode) {
                done()
                this.$emit('close')
                return
            }
            this.$confirm(
                'Are you sure you want to close? All entered information will be cleared.',
                'Close confirmation',
                {
                    confirmButtonText: 'Yes, close',
                    cancelButtonText: 'Keep editing',
                    type: 'warning',
                }
            ).then(() => {
                done()
                this.$emit('close')
            }).catch(() => {})
        },
        onTypeChange() {
            this.$nextTick(() => {
                if (this.$refs.brandedCallerIdForm) {
                    this.$refs.brandedCallerIdForm.clearValidate()
                }
                this.preValidateForm('brandedCallerIdForm')
            })
            this.availableNumbers = []
            this.selectedPhoneNumberIds = []
            this.numberSearch = ''
            this.numberPagination = { page: 1, size: 10, total: 0 }
            this.currentStep = 1
        },
        goToStep2() {
            this.$refs.brandedCallerIdForm.validate((valid) => {
                if (!valid) {
                    this.$message.error('Please fix the validation errors before proceeding')
                    return
                }
                this.currentStep = 2
                this.fetchAvailableNumbers()
            })
        },
        fetchAvailableNumbers(page = 1) {
            this.loadingNumbers = true
            this.numberPagination.page = page
            axios.get(`/api/admin/compliance/${this.company.id}/unregistered-incoming-numbers`, {
                params: {
                    bundle_type: 13,
                    page: this.numberPagination.page,
                    size: this.numberPagination.size,
                    search: this.numberSearch || undefined,
                },
            })
            .then((response) => {
                this.availableNumbers = response.data.data || []
                this.numberPagination.total = response.data.total || 0
                this.numberPagination.page = response.data.current_page || 1
            })
            .catch((err) => {
                this.$root.handleErrors(err)
            })
            .finally(() => {
                this.loadingNumbers = false
            })
        },
        handleNumberSizeChange(size) {
            this.numberPagination.size = size
            this.fetchAvailableNumbers(1)
        },
        handleNumberSelectionChange(selection) {
            this.selectedPhoneNumberIds = selection.map((item) => item.id)
        },
        selectAllNumbers() {
            if (this.$refs.numberTable) {
                this.availableNumbers.forEach((row) => {
                    this.$refs.numberTable.toggleRowSelection(row, true)
                })
            }
        },
        clearNumberSelection() {
            if (this.$refs.numberTable) {
                this.$refs.numberTable.clearSelection()
            }
            this.selectedPhoneNumberIds = []
        },
        handleFileChange(file, fileList) {
            this.form.loa_file = file.raw
            this.fileList = fileList
            this.preValidateForm('brandedCallerIdForm')
        },
        handleFileRemove() {
            this.form.loa_file = null
            this.fileList = []
            this.preValidateForm('brandedCallerIdForm')
        },
        resetForm() {
            this.currentStep = 1
            this.form = this.defaultForm()
            this.fileList = []
            this.availableNumbers = []
            this.selectedPhoneNumberIds = []
            this.numberSearch = ''
            this.numberPagination = { page: 1, size: 10, total: 0 }
            if (this.$refs.brandedCallerIdForm) {
                this.$refs.brandedCallerIdForm.clearValidate()
            }
        },
        loadBundleData() {
            if (!this.bundleId) {
                return
            }

            this.loading = true
            axios
                .get(`/api/admin/compliance/${this.company.id}/branded-caller-id/${this.bundleId}`)
                .then(({data}) => {
                    const registration = data.compliance_registration
                    const bundle = data.branded_caller_id
                    const brandedCalls = bundle?.branded_calls_information?.bundle_attributes || {}
                    this.form.type = registration.type || TYPE_STANDARD
                    this.form.voice_integrity_id = registration.voice_integrity_id
                    this.form.display_name = brandedCalls.branded_calls_display_name || ''
                    this.form.display_long_name = brandedCalls.branded_calls_long_display_name || ''
                    this.form.loa_file_url = registration.loa_file_url || null

                    if (registration.type === TYPE_ENHANCED && bundle) {
                        const brandedCalls = bundle.branded_calls_information?.bundle_attributes || {}
                        const useCase = bundle.bcid_use_case?.bundle_attributes || {}
                        const business = bundle.bcid_business?.bundle_attributes || {}
                        this.form.display_call_reason = brandedCalls.branded_calls_call_reason || ''
                        this.form.display_logo_url = brandedCalls.branded_calls_logo_url || ''
                        this.form.privacy_notice_url = business.privacy_notice_url || ''
                        if (useCase.category) {
                            this.form.use_case = {
                                ...this.form.use_case,
                                category: useCase.category || '',
                                description: useCase.use_case_description || '',
                                consent_description: useCase.consent_description || '',
                                category_apple: useCase.category_apple || '',
                            }
                        }
                    }

                    this.$nextTick(() => {
                        if (this.$refs.brandedCallerIdForm) {
                            this.$refs.brandedCallerIdForm.clearValidate()
                        }
                    })
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response)
                    this.$emit('close')
                })
                .finally(() => {
                    this.loading = false
                })
        },
        submitBrandedCallerId() {
            if (this.currentStep === 1) {
                this.$refs.brandedCallerIdForm.validate((valid) => {
                    if (!valid) {
                        this.$message.error('Please fix the validation errors before submitting')
                        return false
                    }
                    this.doSubmit()
                })
            } else {
                this.doSubmit()
            }
        },
        doSubmit() {
            this.submitting = true

            const formData = new FormData()
            formData.append('type', this.form.type)
            formData.append('display_name', this.form.display_name)
            formData.append('loa_file', this.form.loa_file)

            if (this.form.type === TYPE_STANDARD) {
                formData.append('display_long_name', this.form.display_long_name)
            } else {
                formData.append('display_call_reason', this.form.display_call_reason)
                formData.append('display_logo_url', this.form.display_logo_url)
                formData.append('privacy_notice_url', this.form.privacy_notice_url)
                formData.append('use_case[category]', this.form.use_case.category)
                formData.append('use_case[description]', this.form.use_case.description)
                formData.append('use_case[consent_description]', this.form.use_case.consent_description)
                if (this.form.use_case.category_apple) {
                    formData.append('use_case[category_apple]', this.form.use_case.category_apple)
                }
                this.selectedPhoneNumberIds.forEach((id) => {
                    formData.append('phone_number_ids[]', id)
                })
            }

            axios
                .post(
                    `/api/admin/compliance/${this.company.id}/branded-caller-id/${this.form.voice_integrity_id}`,
                    formData,
                    {headers: {'Content-Type': 'multipart/form-data'}}
                )
                .then((response) => {
                    this.$notify({
                        title: 'Branded Caller ID Submission',
                        message: `Your ${this.form.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard'} Branded Caller ID has been submitted successfully. Review typically takes up to 20 business days.`,
                        type: 'success',
                        duration: 5000,
                        showClose: true,
                    })
                    this.$emit('submitted', response.data.compliance_registration)
                    this.$emit('close')
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response)
                })
                .finally(() => {
                    this.submitting = false
                })
        },
    },
}
</script>

<style scoped>
.w-full {
    width: 100%;
}

.text-blue {
    color: #409eff;
}

.font-weight-bold {
    font-weight: bold;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.mb-3 {
    margin-bottom: 1rem;
}

.mt-3 {
    margin-top: 1rem;
}

.mt-4 {
    margin-top: 1.5rem;
}

.ml-2 {
    margin-left: 0.5rem;
}

.text-muted {
    color: #909399;
}

.section-header {
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 6px;
}

.prerequisites-alert >>> .el-alert__description {
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: none;
}

.type-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
}

.badge-enhanced {
    background-color: #409eff;
}

.badge-standard {
    background-color: #909399;
}
</style>
